import { Component, Input, OnInit } from '@angular/core';
import { ModalController, LoadingController, ActionSheetController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { JavapiService } from 'src/app/services/javapi.service';
import { Metodosui } from 'src/app/services/metodosui';
import { ImageEditorComponent } from '../image-editor/image-editor.component';
import { VerimagenComponent } from '../verimagen/verimagen.component';
import { CameraSource } from '@capacitor/camera';

@Component({
    selector: 'app-inspecdatosareamedidas',
    templateUrl: './inspecdatosareamedidas.component.html',
    styleUrls: ['./inspecdatosareamedidas.component.scss'],
    standalone: false
})
export class InspecdatosareamedidasComponent implements OnInit {

    @Input() medida: any; // If editing
    @Input() areaId!: string; // If creating new
    @Input() defaultTipo?: 'lineal' | 'cuadrada' | 'unidad';

    currentStep: number = 1;
    totalSteps: number = 5;

    currentMedida: any = {
        descripcion: '',
        ancho: 0,
        alto: 0,
        unidad: 'ml',
        foto: null,
        originalFoto: null,
        annotations: [],
        cantidad: 1,
        tipo_medida: 'cuadrada',
        id: null,
        comentario: ''
    };

    /** Lista de medidas/dimensiones en el paso 4 para una misma descripción */
    dimensionesList: Array<{ id?: any; cantidad: number; ancho: number; alto: number; comentario?: string }> = [];

    /** Modo de ingreso: 'dimensiones' (ancho x alto, sin el 2) o 'cuadrado' (total al cuadrado, elevado al 2) */
    modoIngreso: 'dimensiones' | 'cuadrado' = 'dimensiones';

    medidasTablaCantidad: any[] = []; // de lista_tr_medidas_cantidad
    medidasTablaUnidad: any[] = [];   // de lista_tr_medidas_unidad

    // ...



    loading: boolean = false;
    isSaving: boolean = false;

    constructor(
        private modalCtrl: ModalController,
        private loadingCtrl: LoadingController,
        private actionSheetCtrl: ActionSheetController,
        private javapiService: JavapiService,
        private metodosUi: Metodosui
    ) { }

    ngOnInit() {
        if (this.medida) {
            this.loadMedida(this.medida);
        } else if (this.defaultTipo) {
            this.currentMedida.tipo_medida = this.defaultTipo;
            this.modoIngreso = 'dimensiones';
            this.currentMedida.unidad = 'm';
            this.initDefaultDimension();
            setTimeout(() => {
                this.onTypeChange();
            }, 100);
        } else {
            this.modoIngreso = 'dimensiones';
            this.currentMedida.unidad = 'm';
            this.initDefaultDimension();
            setTimeout(() => {
                this.onTypeChange();
            }, 100);
        }
        this.loadCatalogs();
    }

    toBaseUnit(unit: string): string {
        if (!unit) return 'm';
        return unit.replace(/²|2$/g, '').trim();
    }

    toSquareUnit(unit: string): string {
        if (!unit) return 'm²';
        const base = this.toBaseUnit(unit);
        return `${base}²`;
    }

    onModoIngresoChange(event: any) {
        const val = event?.detail?.value;
        if (val) {
            this.modoIngreso = val;
            this.currentMedida.tipo_medida = 'cuadrada';
            if (val === 'cuadrado') {
                this.currentMedida.unidad = this.toSquareUnit(this.currentMedida.unidad);
            } else {
                this.currentMedida.unidad = this.toBaseUnit(this.currentMedida.unidad);
            }
        }
    }

    initDefaultDimension() {
        if (this.dimensionesList.length === 0) {
            this.dimensionesList.push({
                cantidad: 1,
                ancho: null as any,
                alto: null as any,
                comentario: ''
            });
        }
    }

    addDimension() {
        this.dimensionesList.unshift({
            cantidad: 1,
            ancho: null as any,
            alto: null as any,
            comentario: ''
        });
    }

    removeDimension(index: number) {
        if (this.dimensionesList.length > 1) {
            this.dimensionesList.splice(index, 1);
        }
    }

    getMedidaOptions(): any[] {
        if (!this.currentMedida?.tipo_medida) return [];
        if (this.currentMedida.tipo_medida === 'unidad') {
            return this.medidasTablaCantidad;
        }

        // Obtener base units sin duplicados y sin el 2
        const baseSet = new Set<string>();
        for (const u of this.medidasTablaUnidad) {
            const raw = typeof u === 'string' ? u : (u.dt2 || '');
            if (raw) {
                const base = this.toBaseUnit(raw);
                if (base) baseSet.add(base);
            }
        }
        if (baseSet.size === 0) {
            ['cm', 'm', 'pies', 'pulgadas'].forEach(b => baseSet.add(b));
        }

        const isCuadrado = this.currentMedida.tipo_medida === 'cuadrada' && this.modoIngreso === 'cuadrado';
        return Array.from(baseSet).map((base, idx) => {
            const formatted = isCuadrado ? `${base}²` : base;
            return {
                dt1: idx.toString(),
                dt2: formatted
            };
        });
    }

    async loadCatalogs() {
        try {
            // Fetch JSON parameters instead of legacy SQL subroutines
            const [resCant, resUni] = await Promise.all([
                firstValueFrom(this.javapiService.getJsonParam('fe_lista_cantidades')),
                firstValueFrom(this.javapiService.getJsonParam('fe_lista_unidades'))
            ]);

            // Parse and map Quantities (Objects: {nombre, abreviatura})
            if (resCant && (resCant.pa_json || resCant.dt3)) {
                const jsonCant = typeof (resCant.pa_json || resCant.dt3) === 'string' 
                    ? JSON.parse(resCant.pa_json || resCant.dt3) 
                    : (resCant.pa_json || resCant.dt3);
                
                this.medidasTablaCantidad = jsonCant.map((item: any, index: number) => ({
                    dt1: index.toString(),
                    dt2: item.nombre || item, // Use 'nombre' or raw string for backward compatibility
                    dt3: item.abreviatura || ''
                }));
            }

            // Parse and map Units (Array of strings)
            if (resUni && (resUni.pa_json || resUni.dt3)) {
                const jsonUni = typeof (resUni.pa_json || resUni.dt3) === 'string' 
                    ? JSON.parse(resUni.pa_json || resUni.dt3) 
                    : (resUni.pa_json || resUni.dt3);
                
                this.medidasTablaUnidad = jsonUni.map((item: any, index: number) => ({
                    dt1: index.toString(),
                    dt2: item // units are raw strings
                }));
            }
        } catch (e) {
            console.error('Error loading catalogs from JSON', e);
            this.metodosUi.showToast('Error al cargar catálogos de medidas', 'danger');
        }
    }

    dismiss() {
        this.modalCtrl.dismiss();
    }

    async loadMedida(item: any) {
        this.currentMedida = {
            id: item.id,
            descripcion: item.descripcion,
            ancho: item.ancho,
            alto: item.alto,
            unidad: item.unidad,
            foto: null,
            originalFoto: null,
            cantidad: item.cantidad,
            tipo_medida: item.tipo_medida || 'cuadrada',
            comentario: item.comentario || '',
            annotations: []
        };

        if (this.currentMedida.tipo_medida === 'cuadrada') {
            this.modoIngreso = Number(item.alto) <= 0 ? 'cuadrado' : 'dimensiones';
            if (this.modoIngreso === 'cuadrado') {
                this.currentMedida.unidad = this.toSquareUnit(this.currentMedida.unidad);
            } else {
                this.currentMedida.unidad = this.toBaseUnit(this.currentMedida.unidad);
            }
        } else {
            this.modoIngreso = 'dimensiones';
            this.currentMedida.unidad = this.toBaseUnit(this.currentMedida.unidad);
        }

        const qtyVal = Number(item.cantidad) || 1;
        const inputAncho = (this.currentMedida.tipo_medida === 'cuadrada' && this.modoIngreso === 'cuadrado')
            ? (Number(item.ancho) > 0 ? Number(item.ancho) : ((Number(item.area) || 0) / qtyVal))
            : (Number(item.ancho) || 0);

        this.dimensionesList = [{
            id: item.id,
            cantidad: qtyVal,
            ancho: inputAncho,
            alto: Number(item.alto) || 0,
            comentario: item.comentario || ''
        }];

        if (item.foto) {
            if (item.foto.startsWith('data:image')) {
                this.currentMedida.foto = item.foto;
                this.currentMedida.originalFoto = item.foto;
            } else {
                try {
                    // Parallel requests: Image + Metadata + Original Image
                    const imageReq = firstValueFrom(this.javapiService.getImage(item.foto, 'path_img_medidas'));
                    const metaReq = firstValueFrom(this.javapiService.getInspeccionMedidaMetadata(item.foto));

                    const origFilename = item.foto + '_orig';
                    const origReq = firstValueFrom(this.javapiService.getImage(origFilename, 'path_img_medidas'))
                        .catch(() => null);

                    const [base64, metadataJson, origBase64] = await Promise.all([imageReq, metaReq, origReq]);

                    this.currentMedida.foto = base64;
                    this.currentMedida.originalFoto = (origBase64 && origBase64.length > 50) ? origBase64 : base64;

                    if (metadataJson && metadataJson !== '{}') {
                        try {
                            this.currentMedida.annotations = JSON.parse(metadataJson);
                        } catch (err) {
                            console.warn('Invalid metadata JSON', err);
                        }
                    }
                } catch (e) {
                    console.error('Error loading image/metadata', e);
                    this.metodosUi.showToast('No se pudo cargar la imagen', 'warning');
                }
            }
        }
    }

    isValidMedida() {
        if (!this.currentMedida.descripcion || this.currentMedida.descripcion.trim() === '') return false;
        if (!this.currentMedida.unidad) return false;
        if (this.dimensionesList.length === 0) return false;

        for (const dim of this.dimensionesList) {
            const qty = Number(dim.cantidad) || 1;
            if (qty <= 0) return false;
            if (this.currentMedida.tipo_medida === 'lineal') {
                if (Number(dim.ancho) <= 0) return false;
            } else if (this.currentMedida.tipo_medida === 'cuadrada') {
                if (this.modoIngreso === 'cuadrado') {
                    if (Number(dim.ancho) <= 0) return false;
                } else {
                    if (Number(dim.ancho) <= 0 || Number(dim.alto) <= 0) return false;
                }
            }
        }
        return true; 
    }

    validateWithNotify(): boolean {
        if (!this.currentMedida.descripcion || this.currentMedida.descripcion.trim() === '') {
            this.metodosUi.showToast('Debe ingresar una descripción', 'warning');
            return false;
        }
        if (!this.currentMedida.unidad) {
            this.metodosUi.showToast('Debe seleccionar una unidad', 'warning');
            return false;
        }
        if (this.dimensionesList.length === 0) {
            this.metodosUi.showToast('Debe ingresar al menos una medida', 'warning');
            return false;
        }

        for (let i = 0; i < this.dimensionesList.length; i++) {
            const dim = this.dimensionesList[i];
            const num = this.dimensionesList.length > 1 ? ` #${i + 1}` : '';
            if (Number(dim.cantidad) <= 0) {
                this.metodosUi.showToast(`La cantidad en la medida${num} debe ser mayor a 0`, 'warning');
                return false;
            }
            if (this.currentMedida.tipo_medida === 'lineal') {
                if (Number(dim.ancho) <= 0) {
                    this.metodosUi.showToast(`Debe ingresar el largo en la medida${num}`, 'warning');
                    return false;
                }
            } else if (this.currentMedida.tipo_medida === 'cuadrada') {
                if (this.modoIngreso === 'cuadrado') {
                    if (Number(dim.ancho) <= 0) {
                        this.metodosUi.showToast(`Debe ingresar el total al cuadrado en la medida${num}`, 'warning');
                        return false;
                    }
                } else {
                    if (Number(dim.ancho) <= 0) {
                        this.metodosUi.showToast(`Debe ingresar el ancho en la medida${num}`, 'warning');
                        return false;
                    }
                    if (Number(dim.alto) <= 0) {
                        this.metodosUi.showToast(`Debe ingresar el alto en la medida${num}`, 'warning');
                        return false;
                    }
                }
            }
        }
        return true;
    }

    onTypeChange() {
        const type = this.currentMedida.tipo_medida;
        const currentUnit = this.currentMedida.unidad;
        const list = this.getMedidaOptions();

        // Check if current unit is in the current list
        const isValidInCurrentList = list.some(u => u.dt2 === currentUnit);
        
        if (!isValidInCurrentList && list.length > 0) {
            // Priority defaults as requested
            if (type !== 'unidad') {
                // For Lineal/Cuadrada (now from medidasTablaUnidad)
                const hasML = list.find(u => u.dt2.toLowerCase() === 'ml');
                const hasM = list.find(u => u.dt2.toLowerCase() === 'm' || u.dt2.toLowerCase().includes('metros'));
                this.currentMedida.unidad = hasML ? hasML.dt2 : (hasM ? hasM.dt2 : list[0].dt2);
            } else {
                // For Unidad (now from medidasTablaCantidad)
                const hasPz = list.find(u => u.dt2.toLowerCase().includes('pz') || u.dt2.toLowerCase().includes('pieza'));
                this.currentMedida.unidad = hasPz ? hasPz.dt2 : list[0].dt2;
            }
        }
    }

    // ── NAVEGACIÓN DEL WIZARD ─────────────────────────────────────────────────

    nextStep() {
        if (this.currentStep === 5) {
            this.save();
            return;
        }
        if (this.currentStep < this.totalSteps && this.canAdvance()) {
            this.currentStep++;
        }
    }

    prevStep() {
        if (this.currentStep > 1) {
            this.currentStep--;
        }
    }

    canAdvance(): boolean {
        if (this.currentStep === 1) {
            return true; // Foto es opcional
        }
        if (this.currentStep === 2) {
            return !!this.currentMedida.descripcion && this.currentMedida.descripcion.trim() !== '';
        }
        if (this.currentStep === 3) {
            return !!this.currentMedida.unidad;
        }
        if (this.currentStep === 4) {
            if (!this.dimensionesList || this.dimensionesList.length === 0) return false;
            for (const dim of this.dimensionesList) {
                const qty = Number(dim.cantidad) || 1;
                if (qty <= 0) return false;
                if (this.currentMedida.tipo_medida === 'lineal') {
                    if (Number(dim.ancho) <= 0) return false;
                } else if (this.currentMedida.tipo_medida === 'cuadrada') {
                    if (this.modoIngreso === 'cuadrado') {
                        if (Number(dim.ancho) <= 0) return false;
                    } else {
                        if (Number(dim.ancho) <= 0 || Number(dim.alto) <= 0) return false;
                    }
                }
            }
            return true;
        }
        return true;
    }

    // ── PROCESAMIENTO DE FOTO ──────────────────────────────────────────────────

    async selectPhotoSource() {
        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Seleccionar Origen',
            cssClass: 'custom-action-sheet',
            buttons: [
                {
                    text: 'Cámara',
                    icon: 'camera-outline',
                    handler: () => { this.takePhotoSource(CameraSource.Camera); }
                },
                {
                    text: 'Galería',
                    icon: 'images-outline',
                    handler: () => { this.takePhotoSource(CameraSource.Photos); }
                },
                { text: 'Cancelar', icon: 'close-outline', role: 'cancel' }
            ]
        });
        await actionSheet.present();
    }

    async takePhotoSource(source: 'camera' | 'photos' | CameraSource) {
        const camSource = source === 'camera' ? CameraSource.Camera : (source === 'photos' ? CameraSource.Photos : source);
        const image = await this.metodosUi.takePicture(camSource);
        if (image?.base64String) {
            this.currentMedida.originalFoto = image.base64String;
            this.currentMedida.annotations = [];
            this.processEditor(this.currentMedida.originalFoto);
        }
    }

    async takePhoto() {
        // Mantenemos compatibilidad con invocación por defecto
        this.selectPhotoSource();
    }

    async processEditor(base64: string) {
        const props = {
            imageBase64: base64,
            initialElements: this.currentMedida.annotations
        };

        const editor = await this.metodosUi.openModal(ImageEditorComponent, props);
        if (editor) {
            if (typeof editor === 'string') {
                this.currentMedida.foto = editor;
            } else if (editor.base64) {
                this.currentMedida.foto = editor.base64;
                if (editor.elements) this.currentMedida.annotations = editor.elements;
                if (editor.extractedValues && editor.extractedValues.length > 0) {
                    const vals = editor.extractedValues;
                    if (this.dimensionesList.length > 0) {
                        if (vals.length >= 1) this.dimensionesList[0].ancho = vals[0];
                        if (vals.length >= 2) this.dimensionesList[0].alto = vals[1];
                    }
                    if (vals.length >= 1) this.currentMedida.ancho = vals[0];
                    if (vals.length >= 2) this.currentMedida.alto = vals[1];
                }
            }
        } else {
            if (!this.currentMedida.foto) this.currentMedida.foto = base64;
        }
    }

    getPhotoSrc(foto: string | null): string {
        if (!foto) return '';
        if (foto.startsWith('data:image') || foto.startsWith('http')) return foto;
        return 'data:image/jpeg;base64,' + foto;
    }

    async viewImageFull(startInCrop = false) {
        if (!this.currentMedida.foto) return;
        const fullSrc = this.getPhotoSrc(this.currentMedida.foto);

        const modal = await this.modalCtrl.create({
            component: VerimagenComponent,
            componentProps: {
                image: fullSrc,
                title: this.currentMedida.descripcion || 'Foto de Medida',
                enableCrop: true,
                startInCropMode: startInCrop
            },
            cssClass: 'ver-imagen-modal'
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();
        if (data && data.croppedBase64) {
            const cropped = data.croppedBase64;
            const pureBase64 = cropped.includes(',') ? cropped.split(',')[1] : cropped;
            this.currentMedida.foto = pureBase64;
            this.currentMedida.originalFoto = pureBase64;
            this.currentMedida.annotations = [];
        }
    }

    async manageImage() {
        if (!this.currentMedida.foto) return;

        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Opciones de Foto',
            cssClass: 'glassmorphism-action-sheet',
            buttons: [
                {
                    text: 'Editar Líneas / Medidas',
                    icon: 'create-outline',
                    handler: () => {
                        const bg = this.currentMedida.originalFoto || this.currentMedida.foto;
                        this.processEditor(bg);
                    }
                },
                {
                    text: 'Ver Foto Completa',
                    icon: 'expand-outline',
                    handler: () => {
                        this.viewImageFull(false);
                    }
                },
                {
                    text: 'Recortar Foto',
                    icon: 'crop-outline',
                    handler: () => {
                        this.viewImageFull(true);
                    }
                },
                {
                    text: 'Cambiar Foto',
                    icon: 'camera-reverse-outline',
                    handler: () => {
                        this.selectPhotoSource();
                    }
                },
                {
                    text: 'Eliminar Foto',
                    role: 'destructive',
                    icon: 'trash-outline',
                    handler: () => {
                        this.currentMedida.foto = null;
                        this.currentMedida.originalFoto = null;
                        this.currentMedida.annotations = [];
                    }
                },
                { text: 'Cancelar', icon: 'close-outline', role: 'cancel' }
            ]
        });
        await actionSheet.present();
    }

    async save() {
        if (!this.validateWithNotify()) return;

        this.isSaving = true;

        try {
            const usuario = this.getUsuario();
            const promises = this.dimensionesList.map(dim => {
                let w = Number(dim.ancho) || 0;
                let h = Number(dim.alto) || 0;
                let qty = Number(dim.cantidad) || 1;

                let directArea = '';

                if (this.currentMedida.tipo_medida === 'unidad') {
                    w = 0;
                    h = 0;
                } else if (this.currentMedida.tipo_medida === 'lineal') {
                    h = 0;
                } else if (this.currentMedida.tipo_medida === 'cuadrada') {
                    if (this.modoIngreso === 'cuadrado') {
                        directArea = String(w); // Superficie directa ingresada
                        w = 0;
                        h = 0;
                    }
                }

                const payload = {
                    usuario: usuario,
                    subrutina: dim.id ? 'update' : 'insert',
                    datos: {
                        dt1: String(dim.id || '0'),
                        dt2: String(this.areaId),
                        dt3: this.currentMedida.descripcion,
                        dt4: String(w),
                        dt5: String(h),
                        dt6: this.currentMedida.tipo_medida === 'cuadrada' 
                            ? this.toSquareUnit(this.currentMedida.unidad) 
                            : this.currentMedida.unidad,
                        dt7: this.currentMedida.foto || '',
                        dt8: String(qty),
                        dt9: this.currentMedida.tipo_medida,
                        dt11: (dim.comentario && dim.comentario.trim() !== '') ? dim.comentario.trim() : (this.currentMedida.comentario || ''),
                        dt10: JSON.stringify(this.currentMedida.annotations || []),
                        dt12: directArea,
                        dt14: this.currentMedida.originalFoto || ''
                    }
                };

                return firstValueFrom(this.javapiService.saveInspeccionMedida(payload));
            });

            await Promise.all(promises);
            this.metodosUi.showToast('Medidas guardadas con éxito', 'success');
            this.modalCtrl.dismiss({ saved: true });
        } catch (e) {
            console.error(e);
            this.metodosUi.showToast('Error guardando medidas', 'danger');
        } finally {
            this.isSaving = false;
        }
    }

    private getUsuario(): string {
        const userJson = localStorage.getItem('user');
        if (userJson) {
            const user = JSON.parse(userJson);
            if (user.l_usuario) return user.l_usuario;
        }
        return 'system';
    }

}
